import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Figure, Layers } from '../components/Diagram';

interface Scope {
  key: string;
  name: string;
  whenToReach: string;
  body: string;
  example: string;
}

const SCOPES: Scope[] = [
  {
    key: 'GLOBAL_DEFAULT',
    name: 'Global default',
    whenToReach:
      'Every component needs at least one Global default — the fallback used when no more-specific scope applies.',
    body:
      'A Global default Variable Component Price ships the canonical numbers for a component, per currency. Most components will only ever need this one row.',
    example:
      'Seats v2 → Global default → { USD: $5, EUR: €4.50, GBP: £4 }',
  },
  {
    key: 'MODULE',
    name: 'Module override',
    whenToReach:
      'Use when a specific module bundles the component at a different rate than the global default — for example, the “Analytics Module” gives a built-in seat discount.',
    body:
      'Module-scoped prices target a single (Module id, Module version) pair. They apply to any subscription whose pinned module composition includes that exact module version.',
    example:
      'Seats v2 → Module: Analytics v3 → { USD: $4 } (seats inside Analytics are cheaper)',
  },
  {
    key: 'BUNDLE',
    name: 'Bundle override',
    whenToReach:
      'Use to ship plan-level pricing — the Pro Bundle includes seats at one rate, Enterprise at another.',
    body:
      'Bundle-scoped prices target a single (Bundle id, Bundle version) pair and beat module-scoped prices for subscriptions on that bundle.',
    example:
      'Seats v2 → Bundle: Enterprise v1 → { USD: $3, EUR: €2.80 }',
  },
  {
    key: 'SUBSCRIPTION',
    name: 'Subscription override',
    whenToReach:
      'Use for one-off deals you don’t want as a Custom Pricing Agreement — typically while migrating a single customer or testing a new rate.',
    body:
      'Subscription-scoped prices target a single subscription. They are the most specific scope and beat every other scope for that subscription only — which is why they must carry the component’s whole price, not just the figure being changed. If you need a richer override (different model, custom tiers, multi-axis change), prefer a Custom Pricing Agreement.',
    example:
      'Seats v2 (flat) → Subscription: Acme’s prod sub → { USD: $2.50 }\nEvents v3 (3 tiers) → Subscription: Acme’s prod sub → { USD: [$0.010, $0.008, $0.005] }',
  },
];

export function ScopedPricingGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Scope-based pricing — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Scope-based pricing</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          One component. Four <em>scopes</em>.
        </h1>
        <p className="dp-oneliner">
          Variable Components describe the shape of pricing. Variable Component Prices ship the
          numbers. The same component can carry different numbers at the Subscription, Bundle,
          Module, and Global default scopes — Revvly walks them at billing time and picks the
          first match.
        </p>
      </header>

      <section className="dp-section">
        <h2>The resolution order</h2>
        <p className="dp-section-sub">From most specific to least specific.</p>
        <p className="dp-plain">
          When a subscription is being billed, Revvly looks at every Variable Component on the
          subscription and resolves a price for each one. For each component, the resolver walks
          the scopes in this order until it finds a published row with a matching currency:
        </p>
        <ol className="qs-steps" style={{ marginTop: 24 }}>
          <li className="qs-step">
            <span className="qs-phase-num" style={{ marginRight: 12 }}>1</span>
            <div>
              <div className="qs-step-label">Subscription scope</div>
              <div className="qs-step-detail">
                A Variable Component Price targeting this exact subscription. Rarely used —
                Custom Pricing Agreements are usually a better fit.
              </div>
            </div>
          </li>
          <li className="qs-step">
            <span className="qs-phase-num" style={{ marginRight: 12 }}>2</span>
            <div>
              <div className="qs-step-label">Bundle scope</div>
              <div className="qs-step-detail">
                A price targeting the subscription&rsquo;s pinned (Bundle id, Bundle version).
                Only applies if the subscription is bundle-based.
              </div>
            </div>
          </li>
          <li className="qs-step">
            <span className="qs-phase-num" style={{ marginRight: 12 }}>3</span>
            <div>
              <div className="qs-step-label">Module scope</div>
              <div className="qs-step-detail">
                A price targeting any (Module id, Module version) that the subscription pins —
                directly or through its bundle.
              </div>
            </div>
          </li>
          <li className="qs-step">
            <span className="qs-phase-num" style={{ marginRight: 12 }}>4</span>
            <div>
              <div className="qs-step-label">Global default</div>
              <div className="qs-step-detail">
                The fallback price ladder for the component. Required: every component needs
                at least one Global default published.
              </div>
            </div>
          </li>
        </ol>
        <p className="dp-plain" style={{ marginTop: 24 }}>
          <strong>Currency selection.</strong> At each scope, Revvly looks for a row whose
          <code> pricesByCurrency</code> list contains the subscription&rsquo;s currency. A
          Bundle-scope price with only USD won&rsquo;t apply to an EUR subscription — the
          resolver keeps walking. This means you can ship a Bundle override in only the
          currencies where you actually want it, and fall through to Global for the rest.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>Shape matching.</strong> A scope replaces the resolved price rather than
          merging into it, so every row has to carry the monetary fields its component&rsquo;s
          model bills on — one rate per tier for a volume or graduated tiered component, one
          flat fee per step for a stairstep one, both halves of the committed and overage
          pair. A row carrying a single rate for a tiered component is rejected when you save
          it, at every scope, because it could never produce a charge. See{' '}
          <Link to="/docs/guides/override-pricing">overriding component pricing</Link> for
          the field-by-field breakdown.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>Custom Pricing Agreements sit above all of this.</strong> A Published
          <Link to="/docs/concepts/customPricingAgreement"> Custom Pricing Agreement</Link> for
          the same (customer, component) wins even over a Subscription scope. See the
          <Link to="/docs/guides/custom-agreements"> private-deal guide</Link> for the full
          flow.
        </p>
        <Figure
          title="Price resolution order"
          caption="Revvly stops at the first published row that matches the subscription's currency."
        >
          <Layers
            orderLabel="Most specific first"
            rows={[
              { title: 'Custom Pricing Agreement', note: 'Published, for this customer and component.', tone: 'accent' },
              { title: 'Subscription scope', note: 'A price on this exact subscription.' },
              { title: 'Bundle scope', note: 'The pinned bundle and version.' },
              { title: 'Module scope', note: 'A pinned module and version, directly or through the bundle.' },
              { title: 'Global default', note: 'Required fallback for every component.', tone: 'muted' },
            ]}
          />
        </Figure>
      </section>

      <section className="dp-section">
        <h2>The four scopes, with examples</h2>
        {SCOPES.map((s, i) => (
          <div key={s.key} style={{ marginBottom: 36 }}>
            <h3 style={{ fontFamily: 'var(--rv-font-display)', fontSize: 18, marginBottom: 8 }}>
              <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 10 }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              {s.name}
            </h3>
            <p className="dp-plain" style={{ marginBottom: 12 }}>
              <strong>When to reach for it.</strong> {s.whenToReach}
            </p>
            <p className="dp-plain" style={{ marginBottom: 12 }}>{s.body}</p>
            <pre className="api-code" style={{ marginTop: 0 }}>{s.example}</pre>
          </div>
        ))}
      </section>

      <section className="dp-section">
        <h2>Why scopes, not Pricing Rules?</h2>
        <p className="dp-plain">
          The original Revvly pricing model used <Link to="/docs/concepts/pricingRule">Pricing Rules</Link>
          {' '}with a single price per Bundle × Region × Period. Scope-based component pricing
          replaces that with a layered model so you can express the same intent more locally:
          a regional override is just a Bundle-scope price in that region&rsquo;s currency, an
          enterprise rate is a Bundle-scope price on the Enterprise bundle, a one-off discount
          is a Subscription scope or a Custom Pricing Agreement. The pricing engine still
          consults Pricing Rules for legacy subscriptions and for the bundle-pricing calculator,
          but new work should reach for the component model.
        </p>
      </section>

      <section className="dp-section">
        <h2>Next up</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/custom-agreements" style={{ color: 'inherit', textDecoration: 'none' }}>
              Per-customer overrides with Custom Pricing Agreements ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/versioning" style={{ color: 'inherit', textDecoration: 'none' }}>
              How version pinning interacts with scope ›
            </Link>
          </li>
          <li>
            <Link to="/docs/concepts/variableComponentPrice" style={{ color: 'inherit', textDecoration: 'none' }}>
              The Variable Component Price concept ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
